import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';

import TextField from '../components/TextField';
import FullScreenDialog from '../components/FullScreenDialog';
import DatePicker from '../components/DatePicker';
import DateTimePicker from '../components/DateTimePicker';
import Select from '../components/Select';
import Radio from '../components/Radio';

import usePlanList from '../hooks/usePlanList';
import useQueryParams from '../hooks/useQueryParams';
import useSnackbarText from '../hooks/useSnackbarText';
import useMainContext from '../hooks/useMainContext';

import { 
	isInforced,
	submissionStatusOptions,
	submissionModeOptions,
	submissionIsExistingClientOptions,
	submissionHasNomineeOptions,
	submissionCarAssistantOptions,
	submissionMethodOptions, 
} from '../configs/submissions';
import birthdates from '../api/birthdates';
import submissions from '../api/submissions';
import { maxWidth } from '../components/configs';

const dateFormat = 'YYYY-MM-DD';
const queryParamKey = 'update-submission';

const generateDefaultState = () => {
	return {
		birthdateID: null,
		submissionDate: null,
		lifeAssured: '',
		policyNumber: '',
		birthdate: null,
		contact: '',
		planID: '',
		BSA: '',
		status: submissionStatusOptions[0].value,
		issuedDate: null,
		mode: submissionModeOptions[0].value,
		initial: '',
		isExistingClient: submissionIsExistingClientOptions[0].value,
		hasNominee: submissionHasNomineeOptions[0].value,
		carAssistant: submissionCarAssistantOptions[0].value,
		method: submissionMethodOptions[0].value,
	}
};

const useStyles = makeStyles(theme => ({
	content: {
		maxWidth,
		margin: 'auto',
		width: '100%',
		backgroundColor: '#fff',
		padding: 10,
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column'
	},
	loading: {
		marginTop: 50,
		marginBottom: 50,
		color: '#c7c7c7'
	},
	submitButton: {
		width: '100%',
		marginTop: 20,
		marginBottom: 20,
		color: '#fff',
		borderRadius: 6,
		fontWeight: 600,
		backgroundColor: theme.palette.success.main,
		'&:hover': {
			backgroundColor: theme.palette.success.dark,
		}
	}
}));

function UpdateSubmissionDialog(props) {
	const classes = useStyles();
	const planList = usePlanList();
	const handleSnackbarChange = useSnackbarText();
	const [queryParams, setQueryParams] = useQueryParams();
	const [state, _setState] = useState(generateDefaultState);
	const [error, setError] = useState({});
	const [disabled, setDisable] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const { onSubmissionClose } = useMainContext();

	const queryValue = queryParams[queryParamKey];

	const setState = useCallback((newState = {}) => {
		_setState((prevState) => ({
			...prevState,
			...newState,
		}));
	}, []);

	const handleValueCheck = () => {
		let noError = true;
		let error = {};
		if(!state.lifeAssured) {
			noError = false;
			error.lifeAssured = 'Invalid value.';
		}
		// if(!state.policyNumber) {
		// 	noError = false;
		// 	error.policyNumber = 'Invalid value.';
		// }
		if(!state.planID) {
			noError = false;
			error.planID = 'Invalid value.';
		}
		if(!state.BSA) {
			noError = false;
			error.BSA = 'Invalid value.';
		}
		if(!state.initial) {
			noError = false;
			error.initial = 'Invalid value.';
		}
		setError(error);
		return noError;
	};

	const handleChange = (type) => (event, value) => {
		if(value !== undefined) setState({ [type]: value });
		else setState({ [type]: event.target.value });
	};

	const handleSubmit = async () => {
		if(!handleValueCheck()) {
			handleSnackbarChange({ text: 'Invalid inputs', variant: 'error' });
			return window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
		};

		setDisable(true);
		let { data, success } = await submissions.updateSubmission({
			...state,
			submissionID: queryValue,
		});
		setDisable(false);
		if(success) {
			handleSnackbarChange({ text: 'Submission updated', variant: 'success' });
		} else {
			return handleSnackbarChange({ text: data.message, variant: 'error' });
		}

		if(state.birthdateID) {
			await birthdates.updateBirthdate({
				name: state.lifeAssured,
				date: state.birthdate,
				contact: state.contact,
				birthdateID: state.birthdateID,
			});
		}

		if(onSubmissionClose) onSubmissionClose({ ...state, submissionID: parseInt(queryValue) });
		setQueryParams({ 'update-submission': null });
	};

	useEffect(() => {
		_setState(generateDefaultState());
	}, [queryValue]);

	useEffect(() => {
		const submissionID = queryValue;
		const getData = async () => {
			let { data, success } = await submissions.getSubmission({ submissionID });
			if(success) {
				setState({
					...data,
					planID: String(data.planID),
					status: String(data.status),
					mode: String(data.mode),
					isExistingClient: String(data.isExistingClient),
					hasNominee: String(data.hasNominee),
					carAssistant: String(data.carAssistant),
					method: String(data.method),
				});
				setLoaded(true);
			} else {
				return handleSnackbarChange({ text: data.message, variant: 'error' });
			}

			const birthdateResult = await birthdates.getBirthdateBySubmissionID({ submissionID });
			if(birthdateResult.success) {
				setState({
					birthdateID: birthdateResult.data.birthdate.birthdateID,
					birthdate: moment(birthdateResult.data.birthdate.date).format(dateFormat),
					contact: birthdateResult.data.birthdate.contact,
				});
			}
		}

		if(submissionID) getData();
	}, [queryValue, handleSnackbarChange, setState, setLoaded]);

	return (
		<FullScreenDialog 
			title="Update Submission"
			queryParamKey={queryParamKey}
		>
			{!loaded && <div className={classes.loading}>loading</div>}
			{loaded && (
				<div className={classes.content}>
					<DatePicker
						label="Submission Date"
						value={state.submissionDate}
						onChange={handleChange('submissionDate')}
					/>

					<TextField 
						label="Life Assured*"
						value={state.lifeAssured}
						onChange={handleChange('lifeAssured')}
						error={Boolean(error.lifeAssured)}
						helperText={error.lifeAssured}
					/>

					<DateTimePicker
						label="Birthdate"
						value={state.birthdate}
						onChange={handleChange('birthdate')}
						error={Boolean(error.birthdate)}
						helperText={error.birthdate}
					/>

					<TextField 
						label="Contact"
						value={state.contact}
						onChange={handleChange('contact')}
						error={Boolean(error.contact)}
						helperText={error.contact}
					/>

					<TextField 
						label="Policy Number"
						value={state.policyNumber}
						onChange={handleChange('policyNumber')}
						error={Boolean(error.policyNumber)}
						helperText={error.policyNumber}
					/>

					<Select 
						label="Plan*"
						options={planList}
						value={state.planID}
						onChange={handleChange('planID')}
						error={Boolean(error.planID)}
						helperText={error.planID}
						group
					/>

					<TextField 
						label="BSA*"
						value={state.BSA}
						onChange={handleChange('BSA')}
						type="number"
						error={Boolean(error.BSA)}
						helperText={error.BSA}
					/>

					<Select 
						label="Status"
						options={submissionStatusOptions}
						value={state.status}
						onChange={handleChange('status')}
					/>

					{isInforced(state.status) && (
						<DatePicker
							label="Issued Date"
							value={state.issuedDate}
							onChange={handleChange('issuedDate')}
							error={Boolean(error.issuedDate)}
							helperText={error.issuedDate}
							clearable
						/>
					)}

					<Select 
						label="Mode"
						options={submissionModeOptions}
						value={state.mode}
						onChange={handleChange('mode')}
					/>

					<TextField 
						label="Initial*"
						value={state.initial}
						onChange={handleChange('initial')}
						type="number"
						error={Boolean(error.initial)}
						helperText={error.initial}
					/>

					<Radio 
						label="Client"
						options={submissionIsExistingClientOptions}
						value={state.isExistingClient}
						onChange={handleChange('isExistingClient')}
						row
					/>

					<Radio 
						label="Nominee"
						options={submissionHasNomineeOptions}
						value={state.hasNominee}
						onChange={handleChange('hasNominee')}
						row
					/>

					<Radio 
						label="Car Assistant"
						options={submissionCarAssistantOptions}
						value={state.carAssistant}
						onChange={handleChange('carAssistant')}
					/>

					<Select 
						label="Method"
						options={submissionMethodOptions}
						value={state.method}
						onChange={handleChange('method')}
					/>

					<Button
						className={classes.submitButton}
						disabled={disabled}
						variant="contained"
						color="primary"
						onClick={handleSubmit}
					>
						Update
					</Button>
				</div>
			)}
		</FullScreenDialog>
	);
}

export default UpdateSubmissionDialog;
